/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetMatterQuery } from '@/services/api/mattersService';
import axiosInstance from '@/services/axiosConfig';

// Outcome types
type OutcomeType =
  | 'records_sent'
  | 'request_in_processing'
  | 'wrong_number'
  | 'request_not_received'
  | 'patient_not_found'
  | 'unable_to_reach'
  | 'other_issue';

// Sub-enums
type DeliveryMethod = 'mail' | 'fax' | 'email' | 'portal' | 'courier' | 'other';
type ResendMethod = 'fax' | 'email' | 'portal' | 'mail' | 'other';
type UnableToReachOutcome =
  | 'voicemail'
  | 'no_answer'
  | 'busy'
  | 'disconnected'
  | 'long_hold';
type TimeframeUnit = 'days' | 'weeks' | 'business_days';

interface OutcomeOption {
  value: OutcomeType;
  label: string;
  description: string;
  icon: string;
}

const OUTCOME_OPTIONS: OutcomeOption[] = [
  {
    value: 'records_sent',
    label: 'Records Sent',
    description: 'Provider confirmed records have been sent',
    icon: '✅',
  },
  {
    value: 'request_in_processing',
    label: 'Request In Processing',
    description: 'Request received and being processed',
    icon: '⏳',
  },
  {
    value: 'wrong_number',
    label: 'Wrong Number',
    description: 'Reached wrong department or number',
    icon: '📞',
  },
  {
    value: 'request_not_received',
    label: 'Request Not Received',
    description: 'Provider has no record of our request',
    icon: '❌',
  },
  {
    value: 'patient_not_found',
    label: 'Patient Not Found',
    description: 'Patient not in their system',
    icon: '🚫',
  },
  {
    value: 'unable_to_reach',
    label: 'Unable to Reach',
    description: 'Voicemail, no answer, or disconnected',
    icon: '📵',
  },
  {
    value: 'other_issue',
    label: 'Other Issue',
    description: 'Complex situation requiring manual review',
    icon: '⚠️',
  },
];

export const BPOMedicalRecordsOutcomePage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const matterId = searchParams.get('matter_id') || '';
  const taskId = searchParams.get('task_id') || '';
  const treatmentRequestId = searchParams.get('treatment_request_id') || '';
  const workflowId = searchParams.get('workflow_id') || '';
  const toNumber = searchParams.get('to_number') || '';

  const { data: matter } = useGetMatterQuery(matterId, { skip: !matterId });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [selectedOutcome, setSelectedOutcome] =
    React.useState<OutcomeType | null>(null);

  // Records Sent fields
  const [dateSent, setDateSent] = React.useState('');
  const [deliveryMethod, setDeliveryMethod] =
    React.useState<DeliveryMethod>('mail');
  const [deliveryDetails, setDeliveryDetails] = React.useState('');

  // Request In Processing fields
  const [estimatedTimeframeValue, setEstimatedTimeframeValue] = React.useState<
    number | ''
  >('');
  const [estimatedTimeframeUnit, setEstimatedTimeframeUnit] =
    React.useState<TimeframeUnit>('days');
  const [followupContactName, setFollowupContactName] = React.useState('');
  const [followupPhone, setFollowupPhone] = React.useState('');
  const [followupExtension, setFollowupExtension] = React.useState('');

  // Wrong Number fields
  const [correctPhoneNumber, setCorrectPhoneNumber] = React.useState('');
  const [departmentExtension, setDepartmentExtension] = React.useState('');
  const [howObtained, setHowObtained] = React.useState('');

  // Request Not Received fields
  const [resendMethod, setResendMethod] = React.useState<ResendMethod>('fax');
  const [destinationDetails, setDestinationDetails] = React.useState('');
  const [resendFollowupPhone, setResendFollowupPhone] = React.useState('');
  const [daysUntilFollowup, setDaysUntilFollowup] = React.useState<number | ''>(
    '',
  );

  // Patient Not Found fields
  const [patientNotFoundReason, setPatientNotFoundReason] = React.useState('');
  const [representativeName, setRepresentativeName] = React.useState('');
  const [additionalDetails, setAdditionalDetails] = React.useState('');

  // Unable to Reach fields
  const [unableToReachOutcome, setUnableToReachOutcome] =
    React.useState<UnableToReachOutcome>('voicemail');
  const [hoursUntilRetry, setHoursUntilRetry] = React.useState<number | ''>('');

  // Other Issue fields
  const [issueType, setIssueType] = React.useState('');
  const [detailedExplanation, setDetailedExplanation] = React.useState('');
  const [recommendedNextSteps, setRecommendedNextSteps] = React.useState('');
  const [requiresManualReview, setRequiresManualReview] = React.useState(true);

  // Common notes field
  const [notes, setNotes] = React.useState('');

  const buildPayload = () => {
    const base = {
      workflow_id: workflowId,
      task_id: taskId || null,
      matter_id: matterId || null,
      treatment_request_id: treatmentRequestId || null,
      to_number: toNumber,
      outcome: selectedOutcome,
    };

    switch (selectedOutcome) {
      case 'records_sent':
        return {
          ...base,
          records_sent: {
            date_sent: dateSent || null,
            delivery_method: deliveryMethod,
            delivery_details: deliveryDetails || null,
            notes: notes || null,
          },
        };

      case 'request_in_processing':
        return {
          ...base,
          request_in_processing: {
            estimated_timeframe_value: estimatedTimeframeValue || null,
            estimated_timeframe_unit: estimatedTimeframeUnit,
            followup_contact_name: followupContactName || null,
            followup_phone: followupPhone || null,
            followup_extension: followupExtension || null,
            notes: notes || null,
          },
        };

      case 'wrong_number':
        return {
          ...base,
          wrong_number: {
            correct_phone_number: correctPhoneNumber,
            department_extension: departmentExtension || null,
            how_obtained: howObtained || null,
            notes: notes || null,
          },
        };

      case 'request_not_received':
        return {
          ...base,
          request_not_received: {
            resend_method: resendMethod,
            destination_details: destinationDetails || null,
            followup_phone: resendFollowupPhone || null,
            days_until_followup: daysUntilFollowup || null,
            notes: notes || null,
          },
        };

      case 'patient_not_found':
        return {
          ...base,
          patient_not_found: {
            reason: patientNotFoundReason,
            representative_name: representativeName || null,
            additional_details: additionalDetails || null,
          },
        };

      case 'unable_to_reach':
        return {
          ...base,
          unable_to_reach: {
            outcome: unableToReachOutcome,
            hours_until_retry: hoursUntilRetry || null,
            notes: notes || null,
          },
        };

      case 'other_issue':
        return {
          ...base,
          other_issue: {
            issue_type: issueType || null,
            detailed_explanation: detailedExplanation,
            recommended_next_steps: recommendedNextSteps || null,
            requires_manual_review: requiresManualReview,
          },
        };

      default:
        return base;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOutcome) {
      toast.error('Please select an outcome');
      return;
    }

    // Validate required fields based on outcome
    if (selectedOutcome === 'wrong_number' && !correctPhoneNumber.trim()) {
      toast.error('Correct phone number is required');
      return;
    }
    if (
      selectedOutcome === 'patient_not_found' &&
      !patientNotFoundReason.trim()
    ) {
      toast.error('Reason is required for Patient Not Found');
      return;
    }
    if (selectedOutcome === 'other_issue' && !detailedExplanation.trim()) {
      toast.error('Detailed explanation is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      const resp = await axiosInstance.post(
        '/api/integrations/dialpad/bpo-medical-records-outcome',
        payload,
      );
      if (resp.status < 200 || resp.status >= 300) {
        throw new Error('Failed to save outcome');
      }
      setHasSubmitted(true);
      toast.success('Outcome saved successfully');
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to save outcome', { description: err?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500';
  const labelClass = 'block text-xs text-[#9aa3b2] mb-1';

  return (
    <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center p-6">
      <div className="w-full max-w-3xl rounded-xl border border-[#1f2a44] shadow-2xl bg-gradient-to-b from-[#121936] to-[#0f1731] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#1f2a44] bg-[radial-gradient(1200px_300px_at_-10%_-20%,rgba(16,185,129,0.18),transparent_60%)]">
          <h2 className="text-lg font-semibold text-white">
            Medical Records BPO Outcome
          </h2>
          <p className="text-xs text-[#9aa3b2] mt-1">
            Record the result of your medical records follow-up call
          </p>
        </div>

        <div className="p-6">
          {/* Context Card */}
          <div className="mb-6 rounded-lg border border-[#1f2a44] p-4 text-sm text-[#9aa3b2] bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-white font-medium">Matter</span>
                <div>{matter?.name || 'Unknown'}</div>
              </div>
              <div>
                <span className="text-white font-medium">Phone Number</span>
                <div>{toNumber || '—'}</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Outcome Selector */}
            <div>
              <label className={labelClass}>Select Outcome *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {OUTCOME_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSelectedOutcome(option.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      selectedOutcome === option.value
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-[#1f2a44] hover:border-[#2f3a54] bg-[#0a1228]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{option.icon}</span>
                      <span className="text-white font-medium">
                        {option.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#9aa3b2] mt-1 ml-7">
                      {option.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Conditional Fields */}
            {selectedOutcome === 'records_sent' && (
              <div className="space-y-4 p-4 rounded-lg border border-[#1f2a44] bg-[#0a1228]/50">
                <h3 className="text-white font-medium">Records Sent Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Date Sent</label>
                    <input
                      type="date"
                      value={dateSent}
                      onChange={(e) => setDateSent(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Delivery Method *</label>
                    <select
                      value={deliveryMethod}
                      onChange={(e) =>
                        setDeliveryMethod(e.target.value as DeliveryMethod)
                      }
                      className={inputClass}
                    >
                      <option value="mail">Mail</option>
                      <option value="fax">Fax</option>
                      <option value="email">Email</option>
                      <option value="portal">Portal</option>
                      <option value="courier">Courier</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    Delivery Details (tracking number, confirmation, etc.)
                  </label>
                  <input
                    type="text"
                    value={deliveryDetails}
                    onChange={(e) => setDeliveryDetails(e.target.value)}
                    placeholder="e.g., Tracking #, Fax confirmation"
                    className={inputClass}
                  />
                </div>
              </div>
            )}

            {selectedOutcome === 'request_in_processing' && (
              <div className="space-y-4 p-4 rounded-lg border border-[#1f2a44] bg-[#0a1228]/50">
                <h3 className="text-white font-medium">Processing Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Estimated Timeframe</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={estimatedTimeframeValue}
                        onChange={(e) =>
                          setEstimatedTimeframeValue(
                            e.target.value ? parseInt(e.target.value, 10) : '',
                          )
                        }
                        placeholder="7"
                        className={`${inputClass} w-20`}
                      />
                      <select
                        value={estimatedTimeframeUnit}
                        onChange={(e) =>
                          setEstimatedTimeframeUnit(
                            e.target.value as TimeframeUnit,
                          )
                        }
                        className={inputClass}
                      >
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="business_days">Business Days</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Follow-up Contact Name</label>
                    <input
                      type="text"
                      value={followupContactName}
                      onChange={(e) => setFollowupContactName(e.target.value)}
                      placeholder="Contact person's name"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Follow-up Phone</label>
                    <input
                      type="tel"
                      value={followupPhone}
                      onChange={(e) => setFollowupPhone(e.target.value)}
                      placeholder="(555) 555-5555"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Extension</label>
                    <input
                      type="text"
                      value={followupExtension}
                      onChange={(e) => setFollowupExtension(e.target.value)}
                      placeholder="ext. 123"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedOutcome === 'wrong_number' && (
              <div className="space-y-4 p-4 rounded-lg border border-[#1f2a44] bg-[#0a1228]/50">
                <h3 className="text-white font-medium">Correct Contact Info</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Correct Phone Number *</label>
                    <input
                      type="tel"
                      value={correctPhoneNumber}
                      onChange={(e) => setCorrectPhoneNumber(e.target.value)}
                      placeholder="(555) 555-5555"
                      required
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Department / Extension</label>
                    <input
                      type="text"
                      value={departmentExtension}
                      onChange={(e) => setDepartmentExtension(e.target.value)}
                      placeholder="e.g., Medical Records, ext. 456"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    How was the correct number obtained?
                  </label>
                  <input
                    type="text"
                    value={howObtained}
                    onChange={(e) => setHowObtained(e.target.value)}
                    placeholder="e.g., Transferred by front desk"
                    className={inputClass}
                  />
                </div>
              </div>
            )}

            {selectedOutcome === 'request_not_received' && (
              <div className="space-y-4 p-4 rounded-lg border border-[#1f2a44] bg-[#0a1228]/50">
                <h3 className="text-white font-medium">Resend Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>
                      Preferred Resend Method *
                    </label>
                    <select
                      value={resendMethod}
                      onChange={(e) =>
                        setResendMethod(e.target.value as ResendMethod)
                      }
                      className={inputClass}
                    >
                      <option value="fax">Fax</option>
                      <option value="email">Email</option>
                      <option value="portal">Portal</option>
                      <option value="mail">Mail</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>
                      Destination Details (fax #, email, etc.)
                    </label>
                    <input
                      type="text"
                      value={destinationDetails}
                      onChange={(e) => setDestinationDetails(e.target.value)}
                      placeholder="Fax number or email address"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Follow-up Phone</label>
                    <input
                      type="tel"
                      value={resendFollowupPhone}
                      onChange={(e) => setResendFollowupPhone(e.target.value)}
                      placeholder="(555) 555-5555"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Days Until Follow-up</label>
                    <input
                      type="number"
                      min="1"
                      value={daysUntilFollowup}
                      onChange={(e) =>
                        setDaysUntilFollowup(
                          e.target.value ? parseInt(e.target.value, 10) : '',
                        )
                      }
                      placeholder="7"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedOutcome === 'patient_not_found' && (
              <div className="space-y-4 p-4 rounded-lg border border-[#1f2a44] bg-[#0a1228]/50">
                <h3 className="text-white font-medium">
                  Patient Not Found Details
                </h3>
                <div>
                  <label className={labelClass}>Reason *</label>
                  <textarea
                    value={patientNotFoundReason}
                    onChange={(e) => setPatientNotFoundReason(e.target.value)}
                    placeholder="Why was the patient not found in their system?"
                    rows={3}
                    required
                    className={inputClass}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Representative Name</label>
                    <input
                      type="text"
                      value={representativeName}
                      onChange={(e) => setRepresentativeName(e.target.value)}
                      placeholder="Person you spoke with"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Additional Details</label>
                    <input
                      type="text"
                      value={additionalDetails}
                      onChange={(e) => setAdditionalDetails(e.target.value)}
                      placeholder="Any other relevant info"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedOutcome === 'unable_to_reach' && (
              <div className="space-y-4 p-4 rounded-lg border border-[#1f2a44] bg-[#0a1228]/50">
                <h3 className="text-white font-medium">
                  Unable to Reach Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Outcome *</label>
                    <select
                      value={unableToReachOutcome}
                      onChange={(e) =>
                        setUnableToReachOutcome(
                          e.target.value as UnableToReachOutcome,
                        )
                      }
                      className={inputClass}
                    >
                      <option value="voicemail">Left Voicemail</option>
                      <option value="no_answer">No Answer</option>
                      <option value="busy">Line Busy</option>
                      <option value="disconnected">Number Disconnected</option>
                      <option value="long_hold">Extended Hold Time</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Hours Until Retry</label>
                    <input
                      type="number"
                      min="1"
                      value={hoursUntilRetry}
                      onChange={(e) =>
                        setHoursUntilRetry(
                          e.target.value ? parseInt(e.target.value, 10) : '',
                        )
                      }
                      placeholder="4"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedOutcome === 'other_issue' && (
              <div className="space-y-4 p-4 rounded-lg border border-[#1f2a44] bg-[#0a1228]/50">
                <h3 className="text-white font-medium">
                  Complex Issue Details
                </h3>
                <div>
                  <label className={labelClass}>Issue Type</label>
                  <input
                    type="text"
                    value={issueType}
                    onChange={(e) => setIssueType(e.target.value)}
                    placeholder="e.g., Payment dispute, Authorization needed"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Detailed Explanation *</label>
                  <textarea
                    value={detailedExplanation}
                    onChange={(e) => setDetailedExplanation(e.target.value)}
                    placeholder="Describe the situation in detail..."
                    rows={4}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Recommended Next Steps</label>
                  <textarea
                    value={recommendedNextSteps}
                    onChange={(e) => setRecommendedNextSteps(e.target.value)}
                    placeholder="What actions should be taken?"
                    rows={2}
                    className={inputClass}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="requires_manual_review"
                    checked={requiresManualReview}
                    onChange={(e) => setRequiresManualReview(e.target.checked)}
                    className="rounded border-[#1f2a44] bg-[#0a1228] text-emerald-500 focus:ring-emerald-500"
                  />
                  <label
                    htmlFor="requires_manual_review"
                    className="text-sm text-[#9aa3b2]"
                  >
                    Requires manual review (pause automated follow-ups)
                  </label>
                </div>
              </div>
            )}

            {/* Common Notes Field */}
            {selectedOutcome &&
              selectedOutcome !== 'other_issue' &&
              selectedOutcome !== 'patient_not_found' && (
                <div>
                  <label className={labelClass}>Additional Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional information..."
                    rows={2}
                    className={inputClass}
                  />
                </div>
              )}

            {/* Hidden Fields */}
            <input type="hidden" name="workflow_id" value={workflowId} />
            <input type="hidden" name="task_id" value={taskId} />
            <input type="hidden" name="matter_id" value={matterId} />
            <input
              type="hidden"
              name="treatment_request_id"
              value={treatmentRequestId}
            />
            <input type="hidden" name="to_number" value={toNumber} />

            {/* Submit */}
            <div className="pt-2 flex items-center gap-4">
              <button
                type="submit"
                disabled={isSubmitting || !selectedOutcome || hasSubmitted}
                className="inline-flex items-center justify-center rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {hasSubmitted
                  ? '✓ Saved'
                  : isSubmitting
                    ? 'Saving…'
                    : 'Save Outcome'}
              </button>
              <p className="text-xs text-[#9aa3b2]">
                {hasSubmitted
                  ? 'Outcome has been recorded. You can close this page.'
                  : 'This will update the treatment request status and post to the task.'}
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BPOMedicalRecordsOutcomePage;
